/**
 * SignatureDisplay.jsx
 * 
 * Unified signature component used across Clinical Notes and Custom Forms.
 * 
 * Data model: stores only { signer_id, signed_at } — resolves image, name,
 * and credentials at render time from the users array. This ensures signatures
 * always reflect the latest user profile data.
 * 
 * Backward compatible with:
 *   - Old form field format (raw base64 string)
 *   - Old form field format (object with embedded image)
 *   - Note-level signatures (signer_id + signed_at as separate props)
 */

import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';

function resolveSigner(signerId, users) {
  if (!signerId || !users?.length) return null;
  const u = users.find(u => u.id === signerId);
  if (!u) return null;
  return {
    name: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unknown',
    credentials: u.credentials || '',
    signatureImage: u.signature_image || u.signature || null,
  };
}

function normalizeSignatureValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.signer_id) {
    return { signer_id: value.signer_id, signed_at: value.signed_at || null, legacy_image: value.image || null };
  }
  if (typeof value === 'object' && value.image) {
    return { signer_id: value.signer_id || null, signed_at: value.signed_at || null, legacy_image: value.image };
  }
  if (typeof value === 'string' && value.startsWith('data:')) {
    return { signer_id: null, signed_at: null, legacy_image: value };
  }
  return null;
}

function SignedBox({ signerName, credentials, signatureImage, signedAt, legacyImage, onRemove, loadedSignatures, getSignatureUrl, signerId, setLoadedSignatures }) {
  const displayImage = signatureImage || legacyImage || (signerId && loadedSignatures?.[signerId]) || null;

  React.useEffect(() => {
    if (!displayImage && signerId && getSignatureUrl && loadedSignatures && loadedSignatures[signerId] === undefined) {
      getSignatureUrl(signerId).then(url => {
        if (url && setLoadedSignatures) {
          setLoadedSignatures(prev => ({ ...prev, [signerId]: url }));
        }
      });
    }
  }, [displayImage, signerId, getSignatureUrl, loadedSignatures, setLoadedSignatures]);

  const finalImage = displayImage || (signerId && loadedSignatures?.[signerId]) || null;

  return (
    <div className="p-3 bg-green-50 border border-green-200 rounded cursor-pointer" title="Right-click for options">
      {finalImage ? (
        <div className="mb-2 bg-white p-2 border border-gray-200 rounded">
          <img src={finalImage} alt="Signature" className="h-16 w-auto" style={{ imageRendering: 'crisp-edges' }} onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
      ) : (
        <div className="mb-2 p-2 border border-dashed border-gray-300 rounded bg-gray-50 text-center text-gray-500 text-xs">
          No signature image on file
        </div>
      )}
      <p className="text-green-900 flex items-center text-sm font-semibold">
        <span className="mr-2">&#x2705;</span>
        {signerName || 'Signed'}
        {credentials && <span className="text-xs ml-1 text-green-700">{credentials}</span>}
      </p>
      {signedAt && (
        <p className="text-xs text-green-700 mt-1">
          Signed on {new Date(signedAt).toLocaleDateString()} at {new Date(signedAt).toLocaleTimeString()}
        </p>
      )}
      {onRemove && (
        <div className="mt-2">
          <Button type="button" size="sm" variant="outline" className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={onRemove}>
            Remove Signature
          </Button>
        </div>
      )}
    </div>
  );
}

function UnsignedBox({ currentUser, onSign, disabled }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="w-full"
      onClick={onSign}
      disabled={disabled || !currentUser?.id}
    >
      <span className="mr-1">&#9997;&#65039;</span> Sign
    </Button>
  );
}

export function SignatureDisplay({ value, label, required, users = [], currentUser, onChange, disabled = false, testId, loadedSignatures, setLoadedSignatures, getSignatureUrl, onContextMenu }) {
  const sig = normalizeSignatureValue(value);
  const signer = sig?.signer_id ? resolveSigner(sig.signer_id, users) : null;

  const handleSign = () => {
    if (!currentUser?.id) return;
    onChange?.({ signer_id: currentUser.id, signed_at: new Date().toISOString() });
  };

  if (sig) {
    return (
      <div data-testid={testId}>
        {label && <Label className="text-sm font-semibold mb-1 block">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>}
        <div onContextMenu={onContextMenu}>
          <SignedBox signerName={signer?.name} credentials={signer?.credentials} signatureImage={signer?.signatureImage}
            legacyImage={sig.legacy_image} signedAt={sig.signed_at} signerId={sig.signer_id} onRemove={!disabled ? () => onChange?.(null) : null}
            loadedSignatures={loadedSignatures} setLoadedSignatures={setLoadedSignatures} getSignatureUrl={getSignatureUrl} />
        </div>
      </div>
    );
  }

  if (disabled) {
    return (
      <div data-testid={testId}>
        {label && <Label className="text-sm font-semibold mb-1 block">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>}
        <div className="p-3 bg-gray-50 border border-gray-200 rounded text-gray-500 text-sm">
          Not signed
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      {label && <Label className="text-sm font-semibold mb-1 block">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>}
      <UnsignedBox currentUser={currentUser} onSign={handleSign} disabled={disabled} />
    </div>
  );
}

export { normalizeSignatureValue, resolveSigner };
